

import React, { createRef, ReactElement, useContext, useState } from 'react';
import { useParams } from 'react-router-dom';


import { ImageContainer, Svg, SvgMapping } from './components/image';
import { Modal, Onboarding, Sitemap, StatusContainer } from './components/overlay';
import { FavoriteList } from './components/tables';
import ObjectListDropDown from './components/tables/ObjectList/ObjectListDropDown/ObjectListDropDown';
import ObjectView from './components/views/ObjectView/Desktop/ObjectView';
import ObjectViewMobile from './components/views/ObjectView/Mobile/ObjectViewMobile';
import {
    AspectContainer, Content, Main, NoSpace, Sidebar, SidebarContent, SubContent
} from './components/Wrappers';
import SidebarFloorPlan from './components/Wrappers/Sidebar/SidebarFloorPlan/SidebarFloorPlan';

import { AppContext } from './state/app.state';
import { Fullscreen, FullscreenExit } from '@material-ui/icons';
import './_mixin.scss';
import './App.scss';
import { isMobile, isTablet } from './utils/app.helper';
import PoiInfo from './components/image/PoiInfo/PoiInfo';
import SidebarButtonGroup from './components/interaction/buttons/SidebarButtonGroup/SidebarButtonGroup';
import { getPolygonMeasurements } from './components/image/mapping/mapping.helper';
import { useObserver } from 'mobx-react-lite';
import { NavBreadCrumbs } from './components/interaction';
import { IconButton } from '@material-ui/core';
import KeyboardInfo from './components/overlay/info/KeyboardInfo';
import SVGBoxes from './components/image/mapping/SVGBoxes/SVGBoxes';
import ShareModal from './components/overlay/ShareModal/ShareModal';
import BrandingService from './services/branding.service';
import Footnote from './components/overlay/info/Footnote';
import ViewLabels from './components/image/mapping/ViewLabels/ViewLabels';
import FlagCollection from './components/image/marker/Flag/FlagCollection';
import SvgMarkers from './components/image/marker/SvgMarkers/SvgMarkers';
import SidebarPlaceholderText from './components/overlay/SidebarPlaceholderText/SidebarPlaceholderText';
import SunStudyModal from './components/interaction/SunStudyTimeline/SunStudyModal';
import IframeModal from './components/overlay/IframeModal/IframeModal';
import VideoModal from './components/overlay/VideoModal/VideoModal';
import LoadingTredenord from './LoadingTredenord';
import ImageModal from './components/interaction/ImageModal/ImageModal';
import { useQuery } from './state/app.init';
window.getComputedStyle(document.body);



export const hasNavigator = (): any | undefined => {
    const newVariable: any = window.navigator;

    if (newVariable && newVariable.share) return newVariable;

    else return undefined;
};


const App: React.FC = (): ReactElement => {
    const query = useQuery();
    const { pId } = useParams<{ pId: string }>();
    let projectId = (pId && parseInt(pId)) || undefined;
    const contentref = createRef<HTMLDivElement>();
    const { state, favoriteState, loading, filterState, presentationState } = useContext(AppContext);
    const [disableObjectTouchInteraction, setdisableObjectTouchInteraction] = useState<React.TouchEvent>();
    let isMountedReactApp = false;

    if (!pId) {
        const strProjId = document.getElementById('bv-root-element')?.getAttribute('project-id')
            ?.toString();
        projectId = (strProjId !== null && strProjId && parseInt(strProjId)) || undefined;
        isMountedReactApp = !!projectId;
    }

    const onObjectClick = (objectId: number | null): void => {

        if (isMobile() && !state?.selectedObjectId) presentationState?.resetPanzoomTranslate();

        state?.setSelectedObjectId(objectId);

        if (isMobile()) {
            !!presentationState?.activePreview && Array.from(document.getElementsByClassName('bv-mapping-fade')).forEach(o => {
                o.classList.remove('bv-mapping-fade');
                setTimeout(() => o.classList.add('bv-mapping-fade'), 10);
            });

            const mapping = state?.currentView?.mappings.objects.find(mO => mO.mappingEntityId === objectId);
            const measure = mapping && getPolygonMeasurements(mapping);
            presentationState?.setShowSidebarContent(false);
            objectId && state?.setPreviewObjectId(objectId);
            measure && presentationState?.zoomInOnPosition(measure.xMid > 50 ? measure.xMax + 5 : measure.xMin - 5, measure.yMid);
        }
        presentationState?.setShowObjectView(true);
    };



    const onCanvasClick = ((): void => {
        state?.setSelectedObjectId(null);
        isMobile() && presentationState?.setShowSidebarContent(true);
        presentationState?.setShowObjectView(false);
        isMobile() && presentationState?.setShowPoiInfo(false);
        state?.setSelectedPoiId(null);
        isMobile() && presentationState?.resetPanzoomTranslate();
        isMobile() && presentationState?.setActivePreview(false);
    });


    const onOnboardingClick = (): void => {
        if (state?.project?.config?.forceFullscreen) {
            presentationState?.maximize();
        }

        if (isMobile()) {
            presentationState?.setShowSidebarContent(true);
            presentationState?.setShowSitemap(true);
        }
        presentationState?.setShowOnboarding(false);

    };

    const getPoiInfo = () => {
        if (!state?.selectedPoiId) return <></>;

        const selectedPoi = state?.project?.poiContainers.find(p => p.id === state?.selectedPoiId);

        return selectedPoi ? <PoiInfo previewPoi={ selectedPoi} onClose={(): void => {
            state?.setSelectedPoiId(null);
            isMobile() && presentationState?.setShowSidebarContent(true);
            isMobile() && presentationState?.setShowPoiInfo(false);
        }}/> : <></>;
    };

    const onObjectListRowEnter = (objectId: number): void => {
        state?.setPreviewObjectId(objectId);
    };

    const onObjectListRowLeave = (objectId: number): void => {
        state?.setPreviewObjectId(undefined);
    };

    const onObjectViewMobileClose = (): void => {
        presentationState?.setShowObjectView(false);
        presentationState?.setShowSidebarContent(true);
        state?.setPreviewObjectId(undefined);
        const main = document.getElementById('bv-main');

        if (main) {
            main.classList.remove('bv-main--shrink');
            main.classList.remove('bv-main--touch-action-off');
            presentationState?.resetPanzoomTranslate();
        }
    };

    const svgOnTouchStart = (e: React.TouchEvent) => {
        if (e.touches.length > 1)
            setdisableObjectTouchInteraction(e);
    };

    const svgOnTouchEnd = (e: React.TouchEvent) => {

        if (e.target === disableObjectTouchInteraction?.target)
            setdisableObjectTouchInteraction(undefined);
    };

    return useObserver(() => (
        <AspectContainer>
            {state?.project?.branding && BrandingService.init(query.get('bvBr') || state?.project?.branding)}
            <div
                onTouchStart={svgOnTouchStart}
                onTouchEnd={svgOnTouchEnd}
                className={`bv-app-container ${isMobile() ? 'bv-app-container--mobile' : ''}`} id='app-container'
                style={{ alignItems: state?.project ? 'unset' : 'center' }}
                onKeyDown={(evt): void => {
                    if (evt.key.toLowerCase() === 'escape') {
                        presentationState?.showObjectView && presentationState?.setShowObjectView(false);
                        presentationState?.showObjectList && presentationState?.setShowObjectList(false);
                        presentationState?.showFavoriteModal && presentationState?.setShowFavoriteModal(false);
                        presentationState?.showOnboarding && presentationState?.setShowOnboarding(false);
                    }
                }}>
                <Main>
                    <NoSpace className={(isMobile() && presentationState?.showObjectView && 'bv-nospace--shrink') || ''}>
                        <NavBreadCrumbs/>
                        {(!isMobile() && !isTablet() && state?.project?.config?.fullscreen) && <IconButton className={'bv-maximize'} onClick={presentationState?.maximize}>
                            {presentationState?.fullscreen ? <FullscreenExit/> : <Fullscreen/>}
                        </IconButton>}
                        <Content onClick={(): void => presentationState?.setExpandNavBreadcrumbs(false)} id={'content'} ref={contentref} className={(isMobile() && presentationState?.showObjectView) ? 'bv-content-mobile--shrink' : ''}>
                            {state?.currentView?.imageFile.url && <ImageContainer
                                image={state?.currentView.imageFile.url} />}
                            <SubContent className={((isMobile() && state?.currentViewId) && 'bv-subcontent--shrink') || ''}>
                                <SvgMarkers/>
                                <Svg>
                                    <SvgMapping
                                        onCanvasClick={onCanvasClick}
                                        disableObjectTouchInteraction={!!disableObjectTouchInteraction}
                                        onObjectClick={onObjectClick} />
                                </Svg>
                                <FlagCollection/>
                            </SubContent>
                            <ViewLabels/>
                            <SVGBoxes/>
                        </Content>
                    </NoSpace>
                    <Footnote/>
                    <KeyboardInfo/>
                </Main>
                {!isMobile() && <ObjectView
                    show={!!presentationState?.showObjectView}
                    onClose={():void => presentationState && presentationState.setShowObjectView(false)}
                    fullscreen={presentationState?.fullscreen || false}
                    portal={isMountedReactApp && !presentationState?.fullscreen }/>
                }
                {isMobile() && !!presentationState?.showObjectView && !!state?.selectedObjectId && <div>
                    {state?.selectedObjectId && <ObjectViewMobile
                        onClose={onObjectViewMobileClose} />}
                </div>}
                <Sidebar className={presentationState?.showSidebarContent ? 'bv-sidebar--expanded' : ''}>{presentationState?.showSidebarContent && <>
                    <ObjectListDropDown
                        open={presentationState?.showObjectList}
                        setOpen={presentationState?.setShowObjectList}
                        objectMappings={state?.currentView?.mappings.objects.filter(o => {
                            return !!filterState?.filterObjects(state?.objects, state?.project?.objectTypes)?.some(fO => fO.id === o.mappingEntityId);
                        })}
                        selectedObjectId={state?.selectedObjectId}
                        statuses={state?.project?.statuses}
                        setSelectedObjectId={onObjectClick}
                        types={state?.project?.objectTypes}
                        onObjectListRowEnter={onObjectListRowEnter}
                        onObjectListRowLeave={onObjectListRowLeave}
                        fullSize={!document.getElementById('bv-sidebar-button-group')}
                        objects={state?.objects?.filter(o => filterState?.filterObjects(state?.objects, state?.project?.objectTypes)?.some(fO => fO.id === o.id))
                            .filter(o => state?.currentView?.id === state?.project?.primaryViewId ? true : state?.currentView?.mappings.objects.some(cO => cO.mappingEntityId === o.id))}>
                    </ObjectListDropDown>
                    {<Sitemap
                        showSitemap={presentationState?.showSitemap && !presentationState?.showObjectList}
                        sitemapUrl={state?.sitemapUrl || ''}
                        views={state?.project?.views}
                        currentView={state?.currentView}/>}
                    {!presentationState?.showObjectList && <SidebarContent>
                        {
                            !isMobile() &&
                                <SidebarFloorPlan
                                    object={
                                        state?.objects?.find(({ id }) => {
                                            return state.isCurrentPreviewObject(id);
                                        })
                                    }
                                    wide={!document.getElementById('bv-sidebar-button-group')}
                                    fileCollections={state?.fileCollections}
                                ></SidebarFloorPlan>
                        }
                        {presentationState?.showStatusContainer &&
                            <StatusContainer
                                statuses={state?.project?.statuses}
                                showStatusContainer={presentationState?.showStatusContainer}
                                objects={state?.objects}
                            />}
                        {!state?.objects?.find(o => state.isCurrentPreviewObject(o.id)) && !isMobile() && !presentationState?.showStatusContainer &&
                            <SidebarPlaceholderText/>
                        }
                    </SidebarContent>}
                    <FavoriteList
                        onClose={(): void => presentationState?.setShowFavoriteModal(false)}
                        onObjectClick={onObjectClick}
                    />
                    <SidebarButtonGroup projectId={projectId}/></>}
                </Sidebar>

                {state?.selectedPoiId && <Sidebar className={'bv-sidebar--expanded'}>{getPoiInfo()}</Sidebar>}
                {<div className={`bv-forced-background-wrapper ${(presentationState ? presentationState?.showOnboarding : true) ? '' : 'bv-forced-background--fade'}`}><img className={`bv-forced-background ${((presentationState ? presentationState?.showOnboarding : true) ? '' : 'bv-forced-background--fade')}`}
                    src={state?.currentView?.imageFile.url?.large}
                    srcSet={`${state?.currentView?.imageFile.url?.large} 1280w, ${state?.currentView?.imageFile.url?.medium} 800w, ${state?.currentView?.imageFile.url?.small} 480w`} alt=''></img></div>}
                <Onboarding
                    showOnboarding={presentationState ? !!presentationState?.showOnboarding : true}
                    showFinger={true}
                    onClick={onOnboardingClick}>
                    {<StatusContainer sideBar={false} statuses={state?.project?.statuses} objects={state?.objects} showStatusContainer={true} rounded={true} />}
                </Onboarding>
                <SunStudyModal/>
                <VideoModal/>
                <IframeModal/>
                <ShareModal/>
                <ImageModal/>
            </div>
            {state?.project === undefined && <LoadingTredenord/>}
        </AspectContainer>
    ));
};

export default App;

